import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Columns, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { type DadosRelatorioItem, useListarCampos } from '@/services/Relatorios';

interface ModalVisualizarDadosProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dados: DadosRelatorioItem[];
  nomeRelatorio: string;
  relatorioId?: string | null;
}

export function ModalVisualizarDados({
  open,
  onOpenChange,
  dados,
  nomeRelatorio,
  relatorioId,
}: ModalVisualizarDadosProps) {
  const [filtro, setFiltro] = useState('');
  const [colunasVisiveis, setColunasVisiveis] = useState<Set<string>>(new Set());
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);

  const { data: campos } = useListarCampos(relatorioId ?? null, {
    enabled: Boolean(relatorioId),
  });

  const todasColunas = useMemo(() => {
    const primeiroItem = dados[0];
    if (!primeiroItem) return [];

    const chavesDosDados = Object.keys(primeiroItem);

    if (!campos) return chavesDosDados;

    const camposOrdenados = campos
      .filter(campo => campo.Configurado === 'S')
      .sort((a, b) => a.Sequencia_Campo - b.Sequencia_Campo)
      .map(campo => campo.Nome_Campo)
      .filter(nome => chavesDosDados.includes(nome));

    const colunasRestantes = chavesDosDados.filter(chave => !camposOrdenados.includes(chave));

    return [...camposOrdenados, ...colunasRestantes];
  }, [dados, campos]);

  const totalizadores = useMemo(() => {
    if (!campos || dados.length === 0) return null;

    const colunasTotalizadoras = campos
      .filter(campo => campo.Totalizador === 'S' && campo.Configurado === 'S')
      .map(campo => campo.Nome_Campo);

    if (colunasTotalizadoras.length === 0) return null;

    const totais: Record<string, number> = {};
    for (const coluna of colunasTotalizadoras) {
      totais[coluna] = dados.reduce((soma, item) => {
        const valor = item[coluna];
        return soma + (typeof valor === 'number' ? valor : 0);
      }, 0);
    }
    return totais;
  }, [dados, campos]);

  useEffect(() => {
    if (todasColunas.length > 0 && colunasVisiveis.size === 0) {
      setColunasVisiveis(new Set(todasColunas));
    }
  }, [colunasVisiveis.size, todasColunas]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtro, itensPorPagina]);

  const dadosFiltrados = useMemo(() => {
    if (!filtro.trim()) return dados;

    const termo = filtro.toLowerCase();
    return dados.filter(item =>
      Object.values(item).some(value => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(termo);
      })
    );
  }, [dados, filtro]);

  const totalPaginas = Math.max(1, Math.ceil(dadosFiltrados.length / itensPorPagina));
  const inicio = (paginaAtual - 1) * itensPorPagina;
  const dadosPaginados = dadosFiltrados.slice(inicio, inicio + itensPorPagina);

  const toggleColuna = (coluna: string) => {
    setColunasVisiveis(estadoAtual => {
      const novoEstado = new Set(estadoAtual);
      if (novoEstado.has(coluna)) {
        novoEstado.delete(coluna);
      } else {
        novoEstado.add(coluna);
      }
      return novoEstado;
    });
  };

  const formatarValor = (coluna: string, value: DadosRelatorioItem[string]) => {
    if (value === null || value === undefined) return '';

    if (coluna === 'ESC_NUM_DOC') {
      if (typeof value === 'number') {
        return Number.isInteger(value) ? String(value) : String(value).replace(/\.0+$/, '');
      }
      return String(value);
    }

    if (typeof value === 'number') {
      return value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    if (typeof value === 'string' && value.includes('T00:00:00')) {
      return new Date(value).toLocaleDateString('pt-BR');
    }

    return String(value);
  };

  const paginasVisiveis = useMemo(() => {
    const quantidade = Math.min(5, totalPaginas);
    return Array.from({ length: quantidade }, (_, index) => {
      if (totalPaginas <= 5) return index + 1;
      if (paginaAtual <= 3) return index + 1;
      if (paginaAtual >= totalPaginas - 2) return totalPaginas - 4 + index;
      return paginaAtual - 2 + index;
    });
  }, [paginaAtual, totalPaginas]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-[95vw] flex-col overflow-hidden p-0 sm:max-w-[95vw]">
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <DialogTitle className="text-2xl">{nomeRelatorio}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 border-b px-6 py-4">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label htmlFor="filtro-dados" className="mb-2 block text-sm font-medium">
                Pesquisar por todas colunas
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="filtro-dados"
                  placeholder="Digite para filtrar resultados..."
                  value={filtro}
                  onChange={event => setFiltro(event.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Columns className="h-4 w-4" />
                  Colunas ({colunasVisiveis.size}/{todasColunas.length})
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="end">
                <div className="space-y-2">
                  <h4 className="mb-3 text-sm font-medium">Selecionar colunas</h4>
                  <ScrollArea className="h-[300px] pr-4">
                    <div className="space-y-3">
                      {todasColunas.map(coluna => (
                        <div key={coluna} className="flex items-center space-x-2">
                          <Checkbox
                            id={coluna}
                            checked={colunasVisiveis.has(coluna)}
                            onCheckedChange={() => toggleColuna(coluna)}
                          />
                          <label
                            htmlFor={coluna}
                            className="cursor-pointer text-sm font-normal leading-none"
                          >
                            {coluna}
                          </label>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Exibindo {dadosFiltrados.length} de {dados.length} registros
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">Itens por página:</Label>
              <Select
                value={String(itensPorPagina)}
                onValueChange={value => setItensPorPagina(Number(value))}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {totalizadores && (
          <div className="border-b bg-green-50 px-6 py-3">
            <div className="flex flex-wrap gap-6">
              {Object.entries(totalizadores).map(([coluna, total]) => (
                <div key={coluna} className="flex flex-col">
                  <span className="text-xs font-medium text-green-700">{coluna}</span>
                  <span className="text-sm font-semibold text-green-900">
                    {total.toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-hidden px-6">
          <ScrollArea className="h-[calc(90vh-400px)] w-full">
            <div className="min-w-max pb-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    {todasColunas
                      .filter(coluna => colunasVisiveis.has(coluna))
                      .map(coluna => (
                        <TableHead key={coluna} className="whitespace-nowrap font-semibold">
                          {coluna}
                        </TableHead>
                      ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dadosPaginados.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={Math.max(colunasVisiveis.size, 1)}
                        className="py-8 text-center text-muted-foreground"
                      >
                        Nenhum resultado encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    dadosPaginados.map((item, index) => (
                      <TableRow key={`${index}-${Object.values(item).join('-')}`}>
                        {todasColunas
                          .filter(coluna => colunasVisiveis.has(coluna))
                          .map(coluna => (
                            <TableCell key={coluna} className="whitespace-nowrap">
                              {formatarValor(coluna, item[coluna])}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>

        <div className="border-t px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Página {paginaAtual} de {totalPaginas} ({dadosFiltrados.length} registros)
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPaginaAtual(valorAtual => Math.max(1, valorAtual - 1))}
                disabled={paginaAtual === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {paginasVisiveis.map(pagina => (
                  <Button
                    key={pagina}
                    variant={paginaAtual === pagina ? 'default' : 'outline'}
                    size="sm"
                    className="w-10"
                    onClick={() => setPaginaAtual(pagina)}
                  >
                    {pagina}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPaginaAtual(valorAtual => Math.min(totalPaginas, valorAtual + 1))}
                disabled={paginaAtual === totalPaginas}
              >
                Próxima
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
